/**
 * 前端統一錯誤處理工具
 */

// 錯誤類型定義
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// 錯誤嚴重程度
export enum ErrorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

// 前端錯誤類
export class FrontendError extends Error {
  public readonly type: ErrorType
  public readonly severity: ErrorSeverity
  public readonly statusCode?: number
  public readonly details?: any
  public readonly timestamp: string
  public readonly userMessage: string

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN_ERROR,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    statusCode?: number,
    details?: any,
    userMessage?: string
  ) {
    super(message)
    this.name = 'FrontendError'
    this.type = type
    this.severity = severity
    this.statusCode = statusCode
    this.details = details
    this.timestamp = new Date().toISOString()
    this.userMessage = userMessage || this.getDefaultUserMessage()

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FrontendError)
    }
  }

  private getDefaultUserMessage(): string {
    switch (this.type) {
      case ErrorType.NETWORK_ERROR:
        return '網絡連接失敗，請檢查您的網絡連接'
      case ErrorType.AUTHENTICATION_ERROR:
        return '登入已過期，請重新登入'
      case ErrorType.AUTHORIZATION_ERROR:
        return '您沒有權限執行此操作'
      case ErrorType.VALIDATION_ERROR:
        return '輸入的資料有誤，請檢查後重試'
      case ErrorType.NOT_FOUND_ERROR:
        return '找不到請求的資源'
      case ErrorType.SERVER_ERROR:
        return '伺服器發生錯誤，請稍後重試'
      default:
        return '發生未知錯誤，請聯繫技術支援'
    }
  }

  // 序列化為 JSON
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      severity: this.severity,
      statusCode: this.statusCode,
      details: this.details,
      timestamp: this.timestamp,
      userMessage: this.userMessage
    }
  }
}

// 錯誤工廠
export class ErrorFactory {
  static network(message: string = '網絡請求失敗', details?: any): FrontendError {
    return new FrontendError(message, ErrorType.NETWORK_ERROR, ErrorSeverity.HIGH, undefined, details)
  }

  static authentication(message: string = '認證失敗', details?: any): FrontendError {
    return new FrontendError(message, ErrorType.AUTHENTICATION_ERROR, ErrorSeverity.HIGH, 401, details)
  }

  static authorization(message: string = '權限不足', details?: any): FrontendError {
    return new FrontendError(message, ErrorType.AUTHORIZATION_ERROR, ErrorSeverity.HIGH, 403, details)
  }

  static validation(message: string, details?: any): FrontendError {
    return new FrontendError(message, ErrorType.VALIDATION_ERROR, ErrorSeverity.LOW, 400, details)
  }

  static notFound(message: string = '資源不存在', details?: any): FrontendError {
    return new FrontendError(message, ErrorType.NOT_FOUND_ERROR, ErrorSeverity.MEDIUM, 404, details)
  }

  static server(message: string = '伺服器錯誤', statusCode?: number, details?: any): FrontendError {
    return new FrontendError(message, ErrorType.SERVER_ERROR, ErrorSeverity.HIGH, statusCode, details)
  }

  static unknown(message: string = '未知錯誤', details?: any): FrontendError {
    return new FrontendError(message, ErrorType.UNKNOWN_ERROR, ErrorSeverity.MEDIUM, undefined, details)
  }
}

// 從 Axios 錯誤創建前端錯誤
export function fromAxiosError(error: any): FrontendError {
  if (!error.response) {
    // 網絡錯誤
    return ErrorFactory.network('網絡連接失敗，請檢查您的網絡連接', {
      originalError: error.message,
      code: error.code
    })
  }

  const { status, data } = error.response
  const serverMessage = data?.error || data?.message || '伺服器錯誤'

  switch (status) {
    case 400:
      return ErrorFactory.validation(serverMessage, data?.details)
    case 401:
      return ErrorFactory.authentication(serverMessage, data?.details)
    case 403:
      return ErrorFactory.authorization(serverMessage, data?.details)
    case 404:
      return ErrorFactory.notFound(serverMessage, data?.details)
    case 500:
    case 502:
    case 503:
    case 504:
      return ErrorFactory.server(serverMessage, status, data?.details)
    default:
      return ErrorFactory.server(serverMessage, status, data?.details)
  }
}

// 從原始錯誤創建前端錯誤
export function fromError(error: unknown): FrontendError {
  if (error instanceof FrontendError) {
    return error
  }

  if (error instanceof Error) {
    return ErrorFactory.unknown(error.message, { originalError: error.stack })
  }

  return ErrorFactory.unknown('未知錯誤', { originalError: String(error) })
}

// 錯誤報告服務
export interface ErrorReport {
  error: FrontendError
  context?: {
    userId?: string
    userAgent?: string
    url?: string
    timestamp: string
    additionalInfo?: any
  }
}

export class ErrorReportingService {
  private static instance: ErrorReportingService
  private reports: ErrorReport[] = []

  static getInstance(): ErrorReportingService {
    if (!ErrorReportingService.instance) {
      ErrorReportingService.instance = new ErrorReportingService()
    }
    return ErrorReportingService.instance
  }

  report(error: FrontendError, context?: Partial<ErrorReport['context']>) {
    const report: ErrorReport = {
      error,
      context: {
        userId: this.getCurrentUserId(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...context
      }
    }

    this.reports.push(report)

    // 在開發環境中打印詳細錯誤信息
    if (process.env.NODE_ENV === 'development') {
      console.error('🚨 Frontend Error Reported:', report)
    }

    // 只報告高嚴重程度的錯誤到監控服務
    if (error.severity === ErrorSeverity.HIGH || error.severity === ErrorSeverity.CRITICAL) {
      this.sendToMonitoringService(report)
    }
  }

  private getCurrentUserId(): string | undefined {
    // 從 storage 或其他地方獲取用戶 ID
    try {
      const { storage } = require('./storage')
      const token = storage.getItem('accessToken')
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          return payload.userId
        } catch {
          return undefined
        }
      }
    } catch {
      return undefined
    }
    return undefined
  }

  private sendToMonitoringService(report: ErrorReport) {
    // 這裡可以集成第三方錯誤監控服務
    // 例如 Sentry, LogRocket 等

    if (process.env.NODE_ENV === 'production') {
      // 發送到監控服務
      console.log('Sending error to monitoring service:', report.error.type)
    }
  }

  getReports(): ErrorReport[] {
    return [...this.reports]
  }

  clearReports() {
    this.reports = []
  }
}

// 全局錯誤處理器
export function setupGlobalErrorHandling() {
  // 捕獲未處理的 Promise 拒絕
  window.addEventListener('unhandledrejection', (event) => {
    const error = fromError(event.reason)
    ErrorReportingService.getInstance().report(error, {
      additionalInfo: { type: 'unhandledPromiseRejection' }
    })

    // 阻止默認的錯誤處理
    event.preventDefault()
  })

  // 捕獲未處理的 JavaScript 錯誤
  window.addEventListener('error', (event) => {
    const error = ErrorFactory.unknown(event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    })

    ErrorReportingService.getInstance().report(error, {
      additionalInfo: { type: 'unhandledError' }
    })
  })
}

// 錯誤處理 Hook
export function useErrorHandler() {
  const reportError = (error: unknown, context?: Partial<ErrorReport['context']>) => {
    const frontendError = fromError(error)
    ErrorReportingService.getInstance().report(frontendError, context)
    return frontendError
  }

  return { reportError }
}
